import { useCallback, useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Check, X } from "lucide-react";
import { toast } from "sonner";
import { http, errMsg } from "@/lib/api";
import { ConfirmDialog } from "./ConfirmDialog";

const field = "rounded-full border border-fg/10 bg-[var(--ink)] px-4 py-2.5 text-fg outline-none placeholder:text-mist focus:border-[var(--volt)]/60";

const Row = ({ c, onRename, onDelete }) => {
  const [edit, setEdit] = useState(false);
  const [label, setLabel] = useState(c.label);
  const save = async () => { if (await onRename(c, label)) setEdit(false); };
  return (
    <article data-testid={`admin-category-${c.id}`} className="flex items-center gap-3 rounded-3xl border border-fg/10 bg-[var(--ink-2)] p-3 pl-5">
      {edit ? (
        <input autoFocus value={label} onChange={(e) => setLabel(e.target.value)} onKeyDown={(e) => e.key === "Enter" && save()}
          data-testid={`admin-category-rename-input-${c.id}`} className={`${field} min-w-0 flex-1`} />
      ) : (
        <div className="min-w-0 flex-1">
          <p className="truncate font-display text-lg">{c.label}</p>
          <p className="text-xs text-mist">{c.products} prodotti</p>
        </div>
      )}
      {edit ? (
        <>
          <button onClick={save} data-testid={`admin-category-rename-save-${c.id}`} aria-label="Salva" className="flex h-10 w-10 items-center justify-center rounded-full text-[var(--volt)] hover:bg-fg/10"><Check className="h-4 w-4" /></button>
          <button onClick={() => { setEdit(false); setLabel(c.label); }} aria-label="Annulla" data-testid={`admin-category-rename-cancel-${c.id}`} className="flex h-10 w-10 items-center justify-center rounded-full hover:bg-fg/10"><X className="h-4 w-4" /></button>
        </>
      ) : (
        <>
          <button onClick={() => setEdit(true)} data-testid={`admin-category-edit-${c.id}`} aria-label="Rinomina" className="flex h-10 w-10 items-center justify-center rounded-full hover:bg-fg/10"><Pencil className="h-4 w-4" /></button>
          <button onClick={() => onDelete(c)} data-testid={`admin-category-delete-${c.id}`} aria-label="Elimina" className="flex h-10 w-10 items-center justify-center rounded-full text-[var(--flare)] hover:bg-fg/10"><Trash2 className="h-4 w-4" /></button>
        </>
      )}
    </article>
  );
};

export const CategoriesManager = () => {
  const [rows, setRows] = useState([]);
  const [label, setLabel] = useState("");
  const [removing, setRemoving] = useState(null);

  const load = useCallback(() => {
    http.get("/admin/categories").then((r) => setRows(Array.isArray(r.data) ? r.data : [])).catch((e) => toast.error(errMsg(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    try {
      await http.post("/admin/categories", { label });
      toast.success("Categoria aggiunta");
      setLabel("");
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  const rename = async (c, newLabel) => {
    try {
      await http.put(`/admin/categories/${c.id}`, { label: newLabel });
      toast.success("Categoria rinominata");
      load();
      return true;
    } catch (err) {
      toast.error(errMsg(err));
      return false;
    }
  };
  const remove = async () => {
    try {
      await http.delete(`/admin/categories/${removing.id}`);
      toast.success("Categoria eliminata");
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
    setRemoving(null);
  };

  return (
    <div className="mt-4">
      <form onSubmit={add} className="mb-4 flex flex-wrap gap-2" data-testid="admin-category-form">
        <input required minLength={2} maxLength={40} value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Nuova categoria (es. Cover)"
          data-testid="admin-category-name-input" className={`${field} w-full sm:w-72`} />
        <button data-testid="admin-category-add-button" className="flex items-center gap-2 rounded-full bg-[var(--volt)] px-5 py-2.5 text-sm font-semibold text-[var(--ink)] active:scale-95">
          <Plus className="h-4 w-4" /> Aggiungi
        </button>
      </form>
      <p className="mb-4 text-sm text-mist">Per assegnare un prodotto a una categoria, modificalo nella scheda Prodotti. Tutte le categorie sono visibili nel sito, anche quelle ancora vuote.</p>
      <div className="grid gap-3" data-testid="admin-categories-list">
        {rows.map((c) => <Row key={`${c.id}-${c.label}`} c={c} onRename={rename} onDelete={setRemoving} />)}
      </div>
      <ConfirmDialog open={!!removing} testid="admin-category-delete-dialog" onCancel={() => setRemoving(null)} onConfirm={remove}
        title={`Eliminare la categoria ${removing?.label}?`}
        description="Puoi eliminare solo categorie senza prodotti. Se contiene prodotti, spostali prima in un'altra categoria." />
    </div>
  );
};
